import { FC, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { SubmitHandler, useForm } from "react-hook-form";
import { QRCodeSVG } from "qrcode.react";
import InfoAlert from "components/alert/InfoAlert";
import FormError from "components/form/formError";
import FloatingField from "components/form/FloatingField/FloatingField";
import { AccountContext } from "auth/accountProvider";
import { useLogin } from "./useLogin";

type FormValues = {
  verificationCode: string;
};

const SendVerificationCode: FC = () => {
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { loginState, logout, verifyMfaCode } = useContext(AccountContext);

  const { register, handleSubmit } = useForm<FormValues>();

  const { handleRedirect } = useLogin();
  const registration = loginState.mfaRegistration;
  const isSetupMode = Boolean(registration?.uri);

  const handleSendCode: SubmitHandler<FormValues> = async (values) => {
    setIsLoading(true);
    setErrorMessage("");
    try {
      await verifyMfaCode(values.verificationCode);
      setIsLoading(false);
      handleRedirect();
    } catch (error: any) {
      setIsLoading(false);
      setErrorMessage(
        t("Invalid verification code provided, please try again.")
      );
    }
  };

  const handleBack = () => {
    logout();
  };

  let infoText = t("verification code sent to device");
  if (loginState.factor === "otp_email") {
    infoText = t("verification code sent to email");
  }
  if (isSetupMode) {
    infoText = t("mfa setup instructions");
  }

  return (
    <form autoComplete="off" onSubmit={handleSubmit(handleSendCode)}>
      <fieldset disabled={isLoading}>
        <InfoAlert text={infoText} />
        {registration?.uri && (
          <div className="mfa-registration mb-3" data-testid="mfa-registration">
            <div
              className="mfa-registration__qr mb-3"
              style={{
                display: "block",
                textAlign: "center",
                margin: "0 0 12px",
              }}
            >
              <div
                style={{
                  display: "inline-block",
                  padding: 6,
                  border: "3px solid #fff",
                  borderRadius: 18,
                  backgroundColor: "#fff",
                }}
              >
                <QRCodeSVG value={registration.uri} size={180} />
              </div>
            </div>
            <p>{t("mfa setup helper")}</p>
            {registration.secret && (
              <p>
                <strong>{t("Secret key")}:</strong>{" "}
                <code data-testid="mfa-secret">{registration.secret}</code>
              </p>
            )}
          </div>
        )}
        {loginState.factor && loginState.phoneNr && (
          <p>
            <b> {loginState.phoneNr}</b>
          </p>
        )}
        {errorMessage && <FormError>{errorMessage}</FormError>}
        <div className="form-group required">
          <FloatingField
            id="verification-code-input"
            data-testid="verification-code-input"
            type="number"
            label={t("Enter verification code")}
            {...register("verificationCode")}
          />
        </div>

        <div className="form-group">
          <button
            id="submit-send-code"
            type="submit"
            className="btn btn-primary btn-login"
            data-testid="submit-send-code"
          >
            {t("login")}
          </button>
        </div>
        {isLoading && (
          <div
            className="d-flex justify-content-center"
            data-testid="loading-spinner"
          >
            <span className="spinner spinner-default-green" />
          </div>
        )}
        <div className="form-group">
          <div className="row mt-16">
            <div className="col-12">
              <button
                className="btn btn-link"
                id="send-code-back-button"
                type="button"
                onClick={handleBack}
                data-testid="send-code-back-button"
              >
                <span className="vismaicon vismaicon-move-left-circle" />
                {t("back")}
              </button>
            </div>
          </div>
        </div>
      </fieldset>
    </form>
  );
};

export default SendVerificationCode;
